// ไฟล์: frontend/src/pages/UploadResourcePage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import './UploadResourcePage.css';

const UploadResourcePage = ({ user }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [gradeLevels, setGradeLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    file_type: '',
    external_link: '',
    category_id: '',
    subject_id: '',
    grade_level_id: ''
  });
  const [file, setFile] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [errors, setErrors] = useState({});

  // Fetch categories, subjects, and grade levels
  useEffect(() => {
    const fetchFormData = async () => {
      try {
        const [categoriesRes, subjectsRes, gradeLevelsRes] = await Promise.all([
          axios.get('/categories'),
          axios.get('/subjects'),
          axios.get('/grade-levels')
        ]);
        
        setCategories(categoriesRes.data.categories);
        setSubjects(subjectsRes.data.subjects);
        setGradeLevels(gradeLevelsRes.data.gradeLevels);
      } catch (error) {
        console.error('Error fetching form data:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถโหลดข้อมูลฟอร์มได้'
        });
      }
    };

    fetchFormData();
  }, []);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Clear file when file_type changes
    if (name === 'file_type') {
      setFile(null);
      
      if (['video', 'drive', 'link'].includes(value)) {
        document.getElementById('external_link')?.focus();
      }
    }
  };

  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // ตรวจสอบขนาดไฟล์ (ไม่เกิน 15MB)
      if (selectedFile.size > 15 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          file: 'ขนาดไฟล์ต้องไม่เกิน 15MB'
        }));
        return;
      }
      
      setFile(selectedFile);
      setErrors(prev => ({ ...prev, file: '' }));
    }
  };

  // Handle thumbnail change
  const handleThumbnailChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // ตรวจสอบขนาดไฟล์ (ไม่เกิน 5MB)
      if (selectedFile.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          thumbnail: 'ขนาดรูปภาพต้องไม่เกิน 5MB'
        }));
        return;
      }
      
      // ตรวจสอบประเภทไฟล์
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(selectedFile.type)) {
        setErrors(prev => ({
          ...prev,
          thumbnail: 'รองรับเฉพาะไฟล์ JPEG, JPG, และ PNG เท่านั้น'
        }));
        return;
      }
      
      setThumbnail(selectedFile);
      setErrors(prev => ({ ...prev, thumbnail: '' }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'กรุณาระบุชื่อสื่อการสอน';
    if (!formData.description.trim()) newErrors.description = 'กรุณาระบุรายละเอียด';
    if (!formData.file_type) newErrors.file_type = 'กรุณาเลือกประเภทไฟล์';
    if (!formData.category_id) newErrors.category_id = 'กรุณาเลือกหมวดหมู่';
    if (!formData.subject_id) newErrors.subject_id = 'กรุณาเลือกวิชา';
    if (!formData.grade_level_id) newErrors.grade_level_id = 'กรุณาเลือกระดับชั้น';
    
    // Check file or link based on file_type
    if (['pdf', 'ppt', 'other'].includes(formData.file_type)) {
      if (!file) newErrors.file = 'กรุณาอัพโหลดไฟล์';
    } else if (['video', 'drive', 'link'].includes(formData.file_type)) {
      if (!formData.external_link) newErrors.external_link = 'กรุณาระบุลิงก์';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Scroll to first error
      const firstError = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstError);
      if (errorElement) errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('title', formData.title);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('file_type', formData.file_type);
      formDataToSend.append('external_link', formData.external_link);
      formDataToSend.append('category_id', formData.category_id);
      formDataToSend.append('subject_id', formData.subject_id);
      formDataToSend.append('grade_level_id', formData.grade_level_id);
      
      if (file) formDataToSend.append('file', file);
      if (thumbnail) formDataToSend.append('thumbnail', thumbnail);
      
      const response = await axios.post('/resources', formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      Swal.fire({
        icon: 'success',
        title: 'สำเร็จ!',
        text: response.data.message,
        confirmButtonText: 'ตกลง'
      }).then(() => {
        navigate('/dashboard');
      });
      
    } catch (error) {
      console.error('Error uploading resource:', error);
      
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถอัพโหลดสื่อการสอนได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setLoading(false);
    }
  };

  // Get allowed file types based on selected file_type
  const getAllowedFileTypes = () => {
    switch (formData.file_type) {
      case 'pdf':
        return '.pdf';
      case 'ppt':
        return '.ppt,.pptx';
      case 'other':
        return '.doc,.docx,.xls,.xlsx,.pdf,.ppt,.pptx,.zip,.rar';
      default:
        return '*';
    }
  };

  return (
    <div className="upload-resource-page">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-upload"></i> อัพโหลดสื่อการสอน
          </h1>
          <p className="page-description">
            แบ่งปันสื่อการสอนของคุณกับเพื่อนครูทั่วประเทศ
          </p>
        </div>
        
        <div className="card">
          <div className="card-header">
            <i className="fas fa-file-upload"></i> ข้อมูลสื่อการสอน
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="title" className="form-label">ชื่อสื่อการสอน *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`form-control ${errors.title ? 'is-invalid' : ''}`}
                  placeholder="ระบุชื่อสื่อการสอน"
                />
                {errors.title && <div className="invalid-feedback">{errors.title}</div>}
              </div>
              
              <div className="form-group">
                <label htmlFor="description" className="form-label">รายละเอียด *</label>
                <textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  className={`form-control ${errors.description ? 'is-invalid' : ''}`}
                  placeholder="อธิบายรายละเอียดสื่อการสอน"
                  rows="4"
                ></textarea>
                {errors.description && <div className="invalid-feedback">{errors.description}</div>}
              </div>
              
              <div className="form-row">
                <div className="form-group col-md-12">
                  <label htmlFor="file_type" className="form-label">ประเภทไฟล์ *</label>
                  <select
                    id="file_type"
                    name="file_type"
                    value={formData.file_type}
                    onChange={handleChange}
                    className={`form-select ${errors.file_type ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกประเภทไฟล์</option>
                    <option value="pdf">เอกสาร PDF</option>
                    <option value="ppt">นำเสนอ PowerPoint</option>
                    <option value="video">วิดีโอ (ลิงก์)</option>
                    <option value="drive">Google Drive (ลิงก์)</option>
                    <option value="link">ลิงก์ภายนอก</option>
                    <option value="other">ไฟล์อื่นๆ</option>
                  </select>
                  {errors.file_type && <div className="invalid-feedback">{errors.file_type}</div>}
                </div>
                
                <div className="form-group col-md-12">
                  <label htmlFor="category_id" className="form-label">หมวดหมู่ *</label>
                  <select
                    id="category_id"
                    name="category_id"
                    value={formData.category_id}
                    onChange={handleChange}
                    className={`form-select ${errors.category_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกหมวดหมู่</option>
                    {categories.map(category => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                  {errors.category_id && <div className="invalid-feedback">{errors.category_id}</div>}
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group col-md-12">
                  <label htmlFor="subject_id" className="form-label">วิชา *</label>
                  <select
                    id="subject_id"
                    name="subject_id"
                    value={formData.subject_id}
                    onChange={handleChange}
                    className={`form-select ${errors.subject_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกวิชา</option>
                    {subjects.map(subject => (
                      <option key={subject.id} value={subject.id}>{subject.name}</option>
                    ))}
                  </select>
                  {errors.subject_id && <div className="invalid-feedback">{errors.subject_id}</div>}
                </div>
                
                <div className="form-group col-md-12">
                  <label htmlFor="grade_level_id" className="form-label">ระดับชั้น *</label>
                  <select
                    id="grade_level_id"
                    name="grade_level_id"
                    value={formData.grade_level_id}
                    onChange={handleChange}
                    className={`form-select ${errors.grade_level_id ? 'is-invalid' : ''}`}
                  >
                    <option value="">เลือกระดับชั้น</option>
                    {gradeLevels.map(gradeLevel => (
                      <option key={gradeLevel.id} value={gradeLevel.id}>{gradeLevel.name}</option>
                    ))}
                  </select>
                  {errors.grade_level_id && <div className="invalid-feedback">{errors.grade_level_id}</div>}
                </div>
              </div>
              
              {/* Show file upload or external link based on file_type */}
              {['pdf', 'ppt', 'other'].includes(formData.file_type) && (
                <div className="form-group">
                  <label htmlFor="file" className="form-label">อัพโหลดไฟล์ *</label>
                  <input
                    type="file"
                    id="file"
                    name="file"
                    onChange={handleFileChange}
                    className={`form-control ${errors.file ? 'is-invalid' : ''}`}
                    accept={getAllowedFileTypes()}
                  />
                  <small className="form-text text-muted">
                    {formData.file_type === 'pdf' && 'อัพโหลดไฟล์ PDF ขนาดไม่เกิน 15MB'}
                    {formData.file_type === 'ppt' && 'อัพโหลดไฟล์ PowerPoint (.ppt, .pptx) ขนาดไม่เกิน 15MB'}
                    {formData.file_type === 'other' && 'อัพโหลดไฟล์ (.doc, .docx, .xls, .xlsx, .pdf, .ppt, .pptx, .zip, .rar) ขนาดไม่เกิน 15MB'}
                  </small>
                  {errors.file && <div className="invalid-feedback">{errors.file}</div>}
                </div>
              )}
              
              {['video', 'drive', 'link'].includes(formData.file_type) && (
                <div className="form-group">
                  <label htmlFor="external_link" className="form-label">
                    {formData.file_type === 'video' ? 'ลิงก์วิดีโอ *' : 
                     formData.file_type === 'drive' ? 'ลิงก์ Google Drive *' : 'ลิงก์ภายนอก *'}
                  </label>
                  <input
                    type="url"
                    id="external_link"
                    name="external_link"
                    value={formData.external_link}
                    onChange={handleChange}
                    className={`form-control ${errors.external_link ? 'is-invalid' : ''}`}
                    placeholder={
                      formData.file_type === 'video' ? 'https://www.youtube.com/watch?v=...' : 
                      formData.file_type === 'drive' ? 'https://drive.google.com/file/d/...' : 
                      'https://...'
                    }
                  />
                  <small className="form-text text-muted">
                    {formData.file_type === 'video' && 'ระบุลิงก์วิดีโอจาก YouTube, Vimeo หรือแหล่งอื่นๆ'}
                    {formData.file_type === 'drive' && 'ระบุลิงก์ไฟล์จาก Google Drive ที่แชร์เป็นสาธารณะหรือผู้มีลิงก์สามารถเข้าถึงได้'}
                    {formData.file_type === 'link' && 'ระบุลิงก์ภายนอกที่นำไปสู่สื่อการสอน'}
                  </small>
                  {errors.external_link && <div className="invalid-feedback">{errors.external_link}</div>}
                </div>
              )}
              
              <div className="form-group">
                <label htmlFor="thumbnail" className="form-label">รูปภาพหน้าปก (ไม่บังคับ)</label>
                <input
                  type="file"
                  id="thumbnail"
                  name="thumbnail"
                  onChange={handleThumbnailChange}
                  className={`form-control ${errors.thumbnail ? 'is-invalid' : ''}`}
                  accept="image/jpeg,image/png,image/jpg"
                />
                <small className="form-text text-muted">
                  อัพโหลดรูปภาพหน้าปกสำหรับสื่อการสอน (JPEG, PNG) ขนาดไม่เกิน 5MB
                </small>
                {errors.thumbnail && <div className="invalid-feedback">{errors.thumbnail}</div>}
              </div>
              
              <div className="form-notice">
                <div className="notice-content">
                  <i className="fas fa-info-circle"></i>
                  <div>
                    <strong>หมายเหตุ:</strong> สื่อการสอนที่อัปโหลดจะต้องผ่านการอนุมัติจากผู้ดูแลระบบก่อนจึงจะแสดงบนเว็บไซต์
                  </div>
                </div>
              </div>
              
              <div className="form-buttons">
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> กำลังอัพโหลด...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-upload"></i> อัพโหลดสื่อการสอน
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  className="btn btn-light" 
                  onClick={() => navigate(-1)}
                  disabled={loading}
                >
                  <i className="fas fa-arrow-left"></i> ย้อนกลับ
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadResourcePage;